<template>
  <!-- 外层容器：改为全屏布局，适配右侧显示 -->
  <div class="chat-full-container">
    <div class="chat-header">
      <div class="header-left">
        <h3>{{ isGroupChat ? '子沐研究群聊' : '子沐研究' }}</h3>
        <!-- 群聊模式下不显示接收者选择器 -->
        <div class="recipient-selector" v-if="!isGroupChat && chatUsers.length > 0">
          <select v-model="selectedRecipient" class="recipient-select" disabled>
            <option :value="SYSTEM_USER_ID">子沐研究</option>
          </select>
        </div>
      </div>
      <button class="close-btn" @click="handleClose">×</button>
    </div>
    
    <!-- 2. 聊天内容区：全屏高度，支持滚动 -->
    <div class="chat-messages">
      <div class="loading" v-if="isLoading">加载中...</div>
      <div class="no-message" v-else-if="messages.length === 0">
        暂无消息，开始聊天吧～
      </div>
      
      <!-- 消息列表：按时间倒序，自己的消息靠右 -->
      <div 
        class="message" 
        :class="{ 'self-message': msg.isSelf }"
        v-for="msg in messages" 
        :key="msg.id"
        @click="markAsRead(msg.id)"
      >
        <!-- 消息发送者信息 + 未读标记 -->
        <div class="message-header">
          <img :src="msg.senderAvatar" class="sender-avatar"  alt="头像"  v-if="msg.senderAvatar"/>
          <span class="sender-name">{{ msg.senderName }}</span>
          <span class="private-tag" v-if="!isGroupChat"></span>
          <span class="unread-tag" v-if="!msg.isRead && !msg.isSelf"></span>
        </div>
        
        <!-- 消息内容：文本/图片 -->
        <div class="message-content">
          <template v-if="msg.messageType === 'text'">{{ msg.content }}</template>
          <template v-else-if="msg.messageType === 'image'">
            <img :src="msg.content" class="msg-image" alt="聊天图片" />
          </template>
        </div>
        
        <!-- 消息时间 -->
        <div class="message-time">{{ formatTime(msg.sendTime) }}</div>
      </div>
    </div>
    
    <!-- 3. 输入区域：固定在底部 -->
    <div class="chat-input">
      <input 
        type="text" 
        :placeholder="isGroupChat ? '输入群聊消息...' : '输入私聊消息...'" 
        v-model="newMessage"
        @keyup.enter="sendMessage"
        :disabled="isSending"
      />
      <button 
        class="send-btn" 
        @click="sendMessage"
        :disabled="!newMessage.trim() || isSending"
      >
        {{ isSending ? '发送中...' : '发送' }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { chatApi } from '@/api/index.js';
import { useUserStore } from '@/store/userStore.js'; 
import { useRoute } from 'vue-router';

export default {
  name: 'ChatFullComponent',
  props: {
    isWindowActive: {
      type: Boolean,
      default: true
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const userStore = useUserStore();
    const route = useRoute();
    const defaultAvatar = require('@/static/default.png');
    
    // 系统用户ID（一对一聊天的固定接收者）
    const SYSTEM_USER_ID = '100021';
    
    // 根据当前路由判断是否为首页（群聊）
    const isGroupChat = computed(() => {
      return route.name === 'home';
    });
    
    // 当前登录用户信息
    const currentUser = {
      id: userStore.userId || 'self',
      name: userStore.userName || '我'
    };

    // 状态管理
    const chatUsers = ref([]); // 聊天用户列表
    const messages = ref([]); // 所有消息存储
    const newMessage = ref(''); // 新消息输入框
    // 根据是否群聊设置接收者：群聊=0，一对一=系统用户ID
    const selectedRecipient = ref(isGroupChat.value ? 0 : SYSTEM_USER_ID);
    const isLoading = ref(false); // 加载状态
    const isSending = ref(false); // 发送中状态
    const unreadMsgIds = ref(new Set()); // 未读消息ID
    let ws = null; // WebSocket实例

    // 滚动到底部
    const scrollToBottom = () => {
      nextTick(() => {
        const messagesContainer = document.querySelector('.chat-messages');
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      });
    };

    // 监听路由变化，更新接收者和重新加载消息
    watch(() => route.path, () => {
      selectedRecipient.value = isGroupChat.value ? 0 : SYSTEM_USER_ID;
      messages.value = [];
      fetchChatMessages();
    });

    onMounted(() => {
      fetchChatMessages(); // 加载历史消息
      initWebSocket(); // 初始化实时消息连接
    });

    onUnmounted(() => {
      if (ws) ws.close(); // 关闭WebSocket
      if (pollingTimer) {
        clearInterval(pollingTimer); // 清理轮询定时器
        pollingTimer = null;
        //console.log('[轮询] 组件卸载,停止轮询');
      }
    });

    // 加载历史消息
    const fetchChatMessages = async (page_idx = 1, page_size = 50) => {
      isLoading.value = true;
      // 确定recipient_type：群聊=0，一对一=1
      const recipientType = isGroupChat.value ? 0 : 1;
      // 确定user_id：群聊时为0，一对一时为当前登录用户ID
      const userId = isGroupChat.value ? 0 : currentUser.id;
      
      try {
        const res = await chatApi.getChatMessages({
          user_id: userId,
          recipient_type: recipientType,
          page_idx,
          page_size,
        });
        //console.log('获取历史消息成功，原始数据:', res);
        // API拦截器已经返回了res.data,所以这里res就是data对象
        if (res && res.list) {
          //console.log('获取历史消息成功，列表长度:', res.list.length);
          
          // 按时间正序排列(旧消息在前,新消息在后)
          const sortedList = res.list.sort((a, b) => 
            new Date(a.send_time).getTime() - new Date(b.send_time).getTime()
          );
          
          const formattedMsgs = sortedList.map(msg => formatMsg(msg));
          messages.value = page_idx === 1 ? formattedMsgs : [...formattedMsgs, ...messages.value];
          
          // 记录未读消息
          formattedMsgs.forEach(msg => {
            if (!msg.isRead && !msg.isSelf) unreadMsgIds.value.add(msg.id);
          });
          
          // 滚动到底部显示最新消息
          scrollToBottom();
        }
      } catch (err) {
        console.error('获取历史消息失败：', err);
      } finally {
        isLoading.value = false;
      }
    };

    // 获取发送者名称(需要在formatMsg之前定义)
    const getSenderName = (msg) => {
      const senderId = msg.sender_id || msg.senderId;
      
      // 1. 检查是否是当前用户
      if (senderId === currentUser.id) return currentUser.name;
      
      // 2. 检查是否是系统客服
      if (senderId === SYSTEM_USER_ID || senderId === '100021') return '系统客服';
      
      // 3. 使用接口返回的sender_name
      if (msg.sender_name) return msg.sender_name;
      
      // 4. 从chatUsers查找
      const user = chatUsers.value.find(u => u.id === senderId);
      if (user) return user.name;
      
      // 5. 默认显示未知用户
      return '未知用户';
    };

    // 消息格式化
    const formatMsg = (msg) => ({
      id: msg.id,
      senderId: msg.sender_id,
      senderName: getSenderName(msg), // 传入完整msg对象
      recipientId: msg.recipient_id,
      recipientType: msg.recipient_type,
      content: msg.content,
      sendTime: msg.send_time,
      isRead: msg.is_read === 1,
      messageType: msg.message_type || 'text',
      isSelf: msg.sender_id === currentUser.id,
      senderAvatar: msg.sender_avator && msg.sender_avator !== '' ? msg.sender_avator : defaultAvatar,
      recipientAvatar: msg.recipient_avator && msg.recipient_avator !== '' ? msg.recipient_avator : defaultAvatar,
    });

    // 发送消息
    const sendMessage = async () => {
      const content = String(newMessage.value).trim();
      if (!content) {
        alert('请输入消息内容');
        return;
      }

      isSending.value = true;
      const recipientType = isGroupChat.value ? 0 : 1;
      const recipientId = isGroupChat.value ? 0 : SYSTEM_USER_ID;
      
      try {
        const formData = new FormData();
        formData.append('sender_id', currentUser.id);
        formData.append('recipient_id', recipientId);
        formData.append('recipient_type', recipientType);
        formData.append('content', content);
        formData.append('message_type', 'text');
        formData.append('is_deleted', '0');

        const res = await chatApi.sendChatMessages(formData);
        // 发送成功：本地追加消息
        const newMsg = {
          id: res?.id || Date.now().toString(),
          senderId: currentUser.id,
          senderName: currentUser.name,
          recipientId: recipientId,
          recipientType: recipientType,
          content: content,
          sendTime: new Date().toISOString(),
          isRead: true,
          messageType: 'text',
          isSelf: true,
          senderAvatar: userStore.avatars || defaultAvatar
        };
        messages.value.push(newMsg);
        newMessage.value = ''; // 清空输入框
        
        // 滚动到底部显示新消息
        scrollToBottom();
      } catch (err) {
        console.error('发送消息失败：', err);
        alert('发送失败，请重试～');
      } finally {
        isSending.value = false;
      }
    };

    // 初始化WebSocket(降级为轮询方案)
    let wsRetryCount = 0;
    const maxRetries = 5;
    let pollingTimer = null; // 轮询定时器
    const POLLING_INTERVAL = 15000; // 15秒轮询一次(活跃时)
    const isChatWindowActive = ref(props.isWindowActive); // 聊天窗口是否激活
    
    // 监听 props 变化
    watch(() => props.isWindowActive, (newVal) => {
      isChatWindowActive.value = newVal;
      //console.log('[聊天窗口] 状态变化:', newVal ? '激活' : '失焦');
          
      // 窗口关闭时,立即停止轮询
      if (!newVal && pollingTimer) {
        clearInterval(pollingTimer);
        pollingTimer = null;
        //console.log('[轮询] 窗口关闭,立即停止轮询');
      }
    });
    
    const initWebSocket = () => {
      const wsUrl = `wss://ntbapi.bigfund.cn/ws/chat?userId=${currentUser.id}`;
      
      try {
        ws = new WebSocket(wsUrl);

        ws.onopen = () => {
          //console.log('[WebSocket] 实时消息连接已建立');
          wsRetryCount = 0;
          // 停止轮询,改用WebSocket
          if (pollingTimer) {
            clearInterval(pollingTimer);
            pollingTimer = null;
            //console.log('[WebSocket] 停止轮询,使用WebSocket');
          }
          ws.send(JSON.stringify({
            type: 'auth',
            userId: currentUser.id
          }));
        };

        ws.onmessage = (event) => {
          //console.log('[WebSocket] 收到原始消息:', event.data);
          const msg = JSON.parse(event.data);
          //console.log('[WebSocket] 解析后消息:', msg);
          
          const formattedMsg = formatMsg(msg);
          //console.log('[WebSocket] 格式化后消息:', formattedMsg);
          
          // 根据当前模式过滤消息
          const currentRecipientType = isGroupChat.value ? 0 : 1;
          //console.log('[WebSocket] 当前聊天模式:', currentRecipientType === 0 ? '群聊' : '一对一');
          
          if (
            (currentRecipientType === 0 && formattedMsg.recipientType === 0) || 
            (currentRecipientType === 1 && formattedMsg.recipientType === 1 && 
             (formattedMsg.senderId === SYSTEM_USER_ID || formattedMsg.recipientId === SYSTEM_USER_ID))
          ) {
            //console.log('[WebSocket] 消息通过过滤,添加到消息列表');
            messages.value.push(formattedMsg);
            if (!formattedMsg.isSelf) unreadMsgIds.value.add(formattedMsg.id);
            
            // WebSocket收到新消息时也滚动到底部
            scrollToBottom();
          } else {
            //console.log('[WebSocket] 消息被过滤,不显示在当前聊天中');
          }
        };

        ws.onclose = () => {
          //console.log('[WebSocket] 连接关闭,启动轮询方案');
          startPolling(); // WebSocket关闭时启动轮询
          
          if (wsRetryCount < maxRetries) {
            wsRetryCount++;
            //console.log(`[WebSocket] ${wsRetryCount}/${maxRetries} 次重连...`);
            setTimeout(initWebSocket, 3000 * wsRetryCount);
          } else {
            console.warn('[WebSocket] 重连次数已达上限,继续使用轮询方案');
          }
        };

        ws.onerror = (err) => {
          console.error('[WebSocket] 连接错误,降级为轮询方案:', err);
          startPolling(); // WebSocket错误时启动轮询
        };
      } catch (error) {
        console.error('[WebSocket] 初始化失败,使用轮询方案:', error);
        startPolling();
      }
    };
    
    // 轮询方案：定期获取新消息(智能轮询:只在窗口激活时轮询)
    const startPolling = () => {
      if (pollingTimer) return; // 避免重复启动
          
      //console.log('[\u8f6e询] 启动消息轮询,间隔:', POLLING_INTERVAL, 'ms');
          
      pollingTimer = setInterval(async () => {
        // 智能轮询:窗口未激活时跳过本次轮询
        if (!isChatWindowActive.value) {
        //  console.log('[\u8f6e询] \u7a97\u53e3\u672a\u6fc0\u6d3b,\u8df3\u8fc7\u672c\u6b21\u8f6e\u8be2');
          return;
        }
            
        try {
          const recipientType = isGroupChat.value ? 0 : 1;
          const userId = isGroupChat.value ? 0 : currentUser.id;
              
          // 获取最新20条消息(避免漏掉消息)
          const res = await chatApi.getChatMessages({
            user_id: userId,
            recipient_type: recipientType,
            page_idx: 1,
            page_size: 20,
          });
              
          if (res && res.list && res.list.length > 0) {
            // 接口返回的是倒序(最新的在前),需要反转为正序(旧→新)
            const sortedList = [...res.list].reverse();      
            sortedList.forEach(latestMsg => {
              // 多维度检查是否重复消息
              const isDuplicate = messages.value.some(m => {
                // 1. 检查消息ID(如果都有ID)
                if (m.id && latestMsg.id && m.id === latestMsg.id) {
                  return true;
                }
                // 2. 检查内容+发送者+时间(避免临时ID导致的重复)
                const isSameSender = m.senderId === latestMsg.sender_id;
                const isSameContent = m.content === latestMsg.content;
                const isSimilarTime = Math.abs(
                  new Date(m.sendTime).getTime() - new Date(latestMsg.send_time).getTime()
                ) < 3000; // 3秒内认为是同一条消息   
                return isSameSender && isSameContent && isSimilarTime;
              });
              if (!isDuplicate) {
                //console.log('[\u8f6e\u8be2] \u53d1\u73b0\u65b0\u6d88\u606f:', latestMsg);
                const formattedMsg = formatMsg(latestMsg);
                // 直接添加到末尾(因为sortedList已经是正序)
                messages.value.push(formattedMsg);  
                if (!formattedMsg.isSelf) unreadMsgIds.value.add(formattedMsg.id);
                scrollToBottom();
              }
            });
          }
        } catch (error) {
          console.error('[\u8f6e\u8be2] \u83b7\u53d6\u6d88\u606f\u5931\u8d25:', error);
        }
      }, POLLING_INTERVAL);
    };

    // 格式化消息时间
    const formatTime = (timeStr) => {
      const now = new Date();
      const msgTime = new Date(timeStr);
      const nowDate = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
      const msgDate = `${msgTime.getFullYear()}-${msgTime.getMonth() + 1}-${msgTime.getDate()}`;

      if (nowDate === msgDate) {
        return msgTime.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
      }
      const yesterday = new Date(now - 24 * 60 * 60 * 1000);
      const yesterdayDate = `${yesterday.getFullYear()}-${yesterday.getMonth() + 1}-${yesterday.getDate()}`;
      if (yesterdayDate === msgDate) {
        return `昨天 ${msgTime.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}`;
      }
      return msgTime.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
    };

    // 标记消息为已读
    const markAsRead = async (msgId) => {
      if (unreadMsgIds.value.has(msgId)) {
        try {
          const msgIndex = messages.value.findIndex(m => m.id === msgId);
          if (msgIndex !== -1) messages.value[msgIndex].isRead = true;
          unreadMsgIds.value.delete(msgId);
        } catch (err) {
          console.error('标记已读失败：', err);
        }
      }
    };

    // 关闭聊天
    const handleClose = () => {
      // 窗口关闭时设置为非激活状态
      isChatWindowActive.value = false;
          
      // 立即清理轮询定时器
      if (pollingTimer) {
        clearInterval(pollingTimer);
        pollingTimer = null;
        //console.log('[轮询] 窗口关闭,立即停止轮询');
      }
          
      // 关闭 WebSocket
      if (ws) {
        ws.close();
        //console.log('[WebSocket] 窗口关闭,关闭WebSocket连接');
      }
          
      //console.log('[聊天窗口] 关闭,所有轮询已停止');
      emit('close');
    };

    return {
      SYSTEM_USER_ID,
      isGroupChat,
      chatUsers,
      messages,
      newMessage,
      selectedRecipient,
      isLoading,
      isSending,
      handleClose,
      sendMessage,
      markAsRead,
      formatTime
    };
  }
};
</script>

<style scoped>
/* 全屏容器：占满父容器，避免溢出 */
.chat-full-container {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
  overflow: hidden;
}

/* 聊天头部：固定高度，阴影分隔 */
.chat-header {
  padding: 16px 24px;
  background-color: #fff;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
}

.header-left h3 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #333;
}

/* 接收者选择器样式 */
.recipient-selector {
  font-size: 14px;
  display: block !important;
  margin-top: 8px;
}

.recipient-select {
  padding: 6px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f5f5f5;
  color: #999;
  cursor: not-allowed;
  font-size: 14px;
}

/* 关闭按钮：圆形悬浮效果 */
.close-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: #f5f5f5;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.close-btn:hover {
  background-color: #eee;
  color: #ff4d4f;
}

/* 3. 聊天内容区：全屏高度，滚动适配 */
.chat-messages {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  background-color: #f5f7fa;
  scrollbar-width: thin;
}

/* 滚动条美化（Chrome/Safari）- 使用全局统一样式 */
.chat-messages::-webkit-scrollbar {
  width: 6px;
}

.chat-messages::-webkit-scrollbar-track {
  background: transparent;
}

.chat-messages::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
  transition: background-color 0.3s;
}

.chat-messages::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

/* 加载/无消息提示：居中显示 */
.loading, .no-message {
  text-align: center;
  padding: 40px 0;
  color: #999;
  font-size: 14px;
}

/* 4. 消息列表样式：区分自己和他人 */
.message {
  margin-bottom: 20px;
  max-width: 70%;
  display: flex;
  flex-direction: column;
}

/* 自己的消息：靠右对齐 */
.self-message {
  margin-left: auto;
  align-items: flex-end;
}

/* 他人的消息：靠左对齐 */
.message:not(.self-message) {
  margin-right: auto;
  align-items: flex-start;
}

/* 消息头部：发送者名称 + 标签 */
.message-header {
  display: flex;
  align-items: center;
  margin-bottom: 4px;
  font-size: 12px;
}

.sender-name {
  color: #666;
  margin-right: 8px;
}

/* 一对一标签样式 */
.private-tag {
  padding: 1px 6px;
  background-color: #e6f7ff;
  color: #1890ff;
  border-radius: 10px;
  font-size: 10px;
  margin-right: 8px;
}

/* 未读标签：小红点 */
.unread-tag {
  width: 8px;
  height: 8px;
  background-color: #ff4d4f;
  border-radius: 50%;
  margin-left: 8px;
}

/* 消息内容：气泡样式 */
.message-content {
  padding: 10px 16px;
  border-radius: 16px;
  line-height: 1.5;
  font-size: 14px;
  word-break: break-all;
}

/* 他人消息气泡：白色背景 */
.message:not(.self-message) .message-content {
  background-color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border-top-left-radius: 4px;
}

/* 自己消息气泡：蓝色背景 */
.self-message .message-content {
  background-color: #1890ff;
  color: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  border-top-right-radius: 4px;
}

/* 图片消息样式：限制最大宽度 */
.msg-image {
  max-width: 200px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
}

.msg-image:hover {
  transform: scale(1.02);
}

/* 消息时间：居左/居右 */
.message-time {
  margin-top: 4px;
  font-size: 10px;
  color: #999;
}

/* 5. 输入区域：固定在底部 */
.chat-input {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background-color: #fff;
  border-top: 1px solid #eee;
  flex-shrink: 0;
}

/* 输入框样式：圆角，无边框 */
.chat-input input {
  flex: 1;
  padding: 12px 16px;
  border: 1px solid #eee;
  border-radius: 24px;
  font-size: 14px;
  transition: border-color 0.2s;
  outline: none;
}

.chat-input input:focus {
  border-color: #1890ff;
}

/* 发送按钮：蓝色渐变，圆角 */
.send-btn {
  padding: 12px 24px;
  background-color: #1890ff;
  color: #fff;
  border: none;
  border-radius: 24px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-left: 12px;
}

.send-btn:hover {
  background-color: #096dd9;
}

/* 发送中按钮：灰色禁用样式 */
.send-btn:disabled {
  background-color: #eee;
  color: #999;
  cursor: not-allowed;
}

.sender-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  margin-right: 8px;
  object-fit: cover;
}
</style>
